/**
 * OneAppConsentModal — hosts the OneApp (/oa/) customer journey in an iframe for the CSC/VLE flow.
 *
 * Opened (instead of a full-page redirect) when the create response has provider === 'ONEAPP'.
 * Listens for the OneApp postMessage events:
 *   - SUCCESS (data ready) -> calls vleAPI.syncConsent(consentHandle) [debounced; SUCCESS fires
 *     twice] while showing a "Fetching your statement…" spinner, then closes + refreshes the list.
 *   - terminal failure (rejected/exit/timeout/etc.) -> closes + surfaces an error; the VLE can raise
 *     a new request.
 *   - non-terminal (AA_NO_ACCOUNT_FOUND / AA_DISCOVERY_FAILED) -> ignored, journey continues.
 *
 * The consentHandle used for sync comes from the create response (prop), NOT the event payload —
 * OneApp's onetime failure events are bare (no handle). Origin is validated against the iframe URL.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { vleAPI } from '../../../services/vle-api.service';
import { ONEAPP_SUCCESS_CODES, ONEAPP_TERMINAL_FAILURE_CODES } from './types';
import type { VleConsentSyncResponse } from './types';

interface OneAppConsentModalProps {
  webRedirectionUrl: string;
  consentHandle: string;
  onClose: () => void;
  onComplete: (result: VleConsentSyncResponse | null) => void;
  onError: (message: string) => void;
}

// Event-code handling is driven by the OneAppEventCode enum + ONEAPP_SUCCESS_CODES /
// ONEAPP_TERMINAL_FAILURE_CODES sets in ./types (single source of truth). Any code outside those
// two sets is an intermediate state and is ignored so the journey continues inside the iframe.

export const OneAppConsentModal: React.FC<OneAppConsentModalProps> = ({
  webRedirectionUrl, consentHandle, onClose, onComplete, onError,
}) => {
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncedRef = useRef(false);

  // Keep the latest callbacks in a ref so the message-listener effect below does NOT re-register
  // on every parent render (the callbacks are inline lambdas at the call site). This keeps the
  // effect deps stable (expectedOrigin + consentHandle only).
  const handlersRef = useRef({ onClose, onComplete, onError });
  handlersRef.current = { onClose, onComplete, onError };

  // The iframe's origin is the only origin we accept postMessage from.
  const expectedOrigin = useMemo(() => {
    try { return new URL(webRedirectionUrl).origin; } catch { return ''; }
  }, [webRedirectionUrl]);

  useEffect(() => {
    const onMessage = async (event: MessageEvent) => {
      // Fail CLOSED: if we could not derive a valid origin (empty webRedirectionUrl / bad URL),
      // reject every message rather than accepting all origins.
      if (!expectedOrigin || event.origin !== expectedOrigin) return; // reject foreign origins
      const data: any = event.data || {};
      const code: string | undefined =
        data.eventCode || data?.request?.eventCode || data?.data?.request?.eventCode;
      if (!code) return;

      if (ONEAPP_SUCCESS_CODES.has(code)) {
        if (syncedRef.current) return; // debounce — the completion event is posted more than once
        syncedRef.current = true;
        setIsSyncing(true);
        try {
          const result = await vleAPI.syncConsent(consentHandle);
          handlersRef.current.onComplete(result);
        } catch {
          handlersRef.current.onError('Could not fetch the statement data. Please try again.');
        } finally {
          setIsSyncing(false);
          handlersRef.current.onClose();
        }
      } else if (ONEAPP_TERMINAL_FAILURE_CODES.has(code)) {
        handlersRef.current.onError('The request was not completed. Please raise a new request.');
        handlersRef.current.onClose();
      }
      // else (INTERMEDIATE, or any unrecognised code): ignore — the journey continues inside the
      // iframe. Staying permissive means unknown/new SDK codes never break or prematurely end it.
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [expectedOrigin, consentHandle]);

  return (
    <div
      className="equal-ds-root"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        style={{
          position: 'relative', background: '#ffffff', borderRadius: '12px', overflow: 'hidden',
          width: 'min(480px, 100%)', height: 'min(720px, 90vh)', boxShadow: '0 20px 60px rgba(0,0,0,.35)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>Bank statement</span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px', borderRadius: '6px', display: 'flex' }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ position: 'relative', flex: 1 }}>
          {(isIframeLoading || isSyncing) && (
            <div style={{ position: 'absolute', inset: 0, background: '#ffffff', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', border: '4px solid #bfdbfe', borderTopColor: '#2563eb', borderRadius: '9999px', animation: 'spin 1s linear infinite' }} />
                <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                  {isSyncing ? 'Fetching your statement…' : 'Loading…'}
                </p>
              </div>
            </div>
          )}
          <iframe
            src={webRedirectionUrl}
            title="OneApp Consent Journey"
            style={{ width: '100%', height: '100%', border: 0 }}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            onLoad={() => setIsIframeLoading(false)}
          />
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
